import { useEffect, useState } from 'react';
import { getLocationManagers } from '../../components/location/requests/location-requests';
import { getErrorMessage } from '../../util/NetworkErrorUtil';
import { LocationManagerData } from '../../views/create-location-wizard/types';

/**
 * Module-level cache of fetched manager lists keyed by account id.
 *
 * The Create/Edit Location wizard keeps step components mounted (inactive ones
 * are hidden), and this hook may also be re-mounted if the supplier leaves and
 * returns to the wizard. Caching here avoids redundant network calls and
 * prevents a visible loading flash when the hook is used again.
 */
const managersCache = new Map<string, LocationManagerData[]>();

/** In-flight requests keyed by account id so concurrent mounts share one fetch. */
const inFlight = new Map<string, Promise<LocationManagerData[]>>();

/**
 * Drops the cached manager list so the next {@link useLocationManagers} mount
 * refetches. The wizard calls this on open so the dropdown — and the phone that
 * gets copied onto the selected manager — reflect any Manage Users edits made
 * since the list was last loaded. Omit `accountId` to clear every account.
 */
export function invalidateLocationManagers(accountId?: string): void {
  if (accountId) {
    managersCache.delete(accountId);
    inFlight.delete(accountId);
  } else {
    managersCache.clear();
    inFlight.clear();
  }
}

interface UseLocationManagersResult {
  managers: LocationManagerData[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads the list of users eligible to be a location manager for an account.
 *
 * Results are cached for the lifetime of the page so navigating back and forth
 * between wizard steps does not re-fetch or flash a loading state.
 *
 * @param accountId The supplier account id (no fetch occurs when undefined)
 */
export function useLocationManagers(accountId: string | undefined): UseLocationManagersResult {
  const cached = accountId ? managersCache.get(accountId) : undefined;

  const [managers, setManagers] = useState<LocationManagerData[]>(cached || []);
  // Only show a loading state when we have nothing cached to render yet.
  const [loading, setLoading] = useState<boolean>(Boolean(accountId) && !cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }

    // Serve cached data immediately, skipping the network round-trip.
    const existing = managersCache.get(accountId);
    if (existing) {
      setManagers(existing);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    // Reuse an in-flight request if another mount already started one.
    const request =
      inFlight.get(accountId) ||
      getLocationManagers(accountId)
        .then((res: { text: string }) => {
          const parsed: LocationManagerData[] = JSON.parse(res.text);
          managersCache.set(accountId, parsed);
          return parsed;
        })
        .finally(() => {
          inFlight.delete(accountId);
        });

    inFlight.set(accountId, request);

    request
      .then((parsed) => {
        if (!cancelled) setManagers(parsed);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(getErrorMessage(err, 'Failed to load location managers. Please try again.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return { managers, loading, error };
}

